import { memo, useState, useCallback, useEffect } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Typography, Drawer, Box, Button } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
// Local files
import FilePreview from 'components/Common/FilePreview/FilePreview';
import DrawerSkeleton from 'components/Common/BaseDrawerSkeleton/BaseDrawerSkeleton';
import BaseEventsList from 'components/Analyses/Common/BaseEventsList/BaseEventsList';
import ErrorWrapper from 'components/Common/ErrorWrapper/ErrorWrapper';
import useCustomSelector from 'hooks/useCustomSelector';
import useFillAnalyses from 'hooks/useFillAnalyses';
import useEvents from 'hooks/useEvents';
import { eventsSelectors } from "features/events";
import { fillAnalysesSelectors } from 'features/fillAnalyses';

const FillAnalysisDrawer = () => {
  const mediaDesktop = useMediaQuery(theme => theme.breakpoints.up('md'));
  const navigate = useNavigate();
  const { fill_analysis_id } = useParams();
  const { getFillAnalysis, clearLocalActiveFillAnalysis } = useFillAnalyses();
  const { getFillAnalysesEvents, clearLocalAnalysesEvents } = useEvents();
  const { data, pagination } = useCustomSelector(eventsSelectors.getAnalysesEventsData);
  const eventsFetching = useCustomSelector(eventsSelectors.analysesEventsIsFetching);
  const fillAnalysisFetching = useCustomSelector(fillAnalysesSelectors.fillAnalysisIsFetching);
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState(null);
  const [result, setResult] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [firstTimeEventsOpened, setFirstTimeEventsOpened] = useState(true);

  const fetchFillAnalysis = useCallback((id) => {
    setOpen(true);
    getFillAnalysis({ id })
    .then(({ payload, error }) => {
      if (!error) {
        const { data: { fill_analysis } } = payload;
        handleSetData(fill_analysis);
      }
    })
  }, [getFillAnalysis]);

  const handleSetData = (fill_analysis) => {
    setInput(fill_analysis?.input);
    setResult(fill_analysis?.result);
  };

  const handleExiting = () => {
    setInput(null);
    setResult(null);
    setExpanded(false);
    setFirstTimeEventsOpened(true);
    navigate(`/flow/fl99f`);
    clearLocalActiveFillAnalysis();
    clearLocalAnalysesEvents();
  };
  
  const handleClose = () => setOpen(false);

  const fetchFillAnalysesEvents = useCallback((offset, fill_analysis_id) => {
    getFillAnalysesEvents({ offset, fill_analysis_id });
  }, [ getFillAnalysesEvents]);

  const handleChangeExpand = (_, isExpanded) => {
    setExpanded(isExpanded);
    if (firstTimeEventsOpened) {
      fetchFillAnalysesEvents(0, fill_analysis_id);
      setFirstTimeEventsOpened(false);
    }
  }

  useEffect(() => {
    if (!!fill_analysis_id) fetchFillAnalysis(fill_analysis_id);
  }, [fill_analysis_id, fetchFillAnalysis]);

  return (
    <Drawer
      anchor={!mediaDesktop ? 'bottom' : 'right'}
      open={open}
      SlideProps={{ onExited: handleExiting }}
      onClose={handleClose}
      ModalProps={{ sx: { zIndex: 1300 } }}
      PaperProps={{
        sx: {
          height: '100%',
          width: { xs: '100%', md: '600px'},
          backgroundColor: 'background.default'
        }
      }}
    >
      <Box>
        <Box sx={{ display: 'flex', p: '8px 8px 0' }}>
          <Button color='inherit' sx={{ fontWeight: 700 }} onClick={handleClose}>Close</Button>
          <div style={{ flexGrow: 1 }}/>
        </Box>
        <Box sx={{ fontSize: '2rem', fontWeight: 'bold', textAlign: 'center'}}>
            Request log
        </Box>
      </Box>
      <Box sx={{ flexGrow: 1, p: { xs: 2, md: '16px 100px 32px' } }}>
        {fillAnalysisFetching ? <DrawerSkeleton /> :
        <>
          <Typography variant='body1' sx={{ fontWeight: 600, textAlign: 'center', mb: '10px' }}>Job Id: {fill_analysis_id}</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center'  }}>
            {input?.id && <Box sx={{ mb: '30px' }}>
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 400, mb: '10px' }}>Input:</Typography>
              <FilePreview
                key={input?.id}
                type={input?.content_type}
                name={input?.filename}
                url={input?.url}
              />
            </Box>}
            {result?.id && <Box sx={{ mb: '30px' }}>
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 400, mb: '10px' }}>Result:</Typography>
              <FilePreview
                key={result?.id}
                type={result?.content_type}
                name={result?.filename}
                url={result?.url}
              />
            </Box>}
          </Box>
          <BaseEventsList
            data={data}
            pagination={pagination}
            analysisId={fill_analysis_id}
            eventsFetching={eventsFetching}
            fetchEvents={fetchFillAnalysesEvents}
            expanded={expanded}
            handleChangeExpand={handleChangeExpand}
          />
        </>}
      </Box>
    </Drawer>
  );
};

const WrappedComponent = props => {
  return (
    <ErrorWrapper>
      <FillAnalysisDrawer {...props} />
    </ErrorWrapper>
  );
};

export default memo(WrappedComponent);